import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, Clock, X, AlertCircle } from 'lucide-react';

export interface UpdateChargingModalProps {
  chargerName: string;
  estimatedEndTime?: string;
  maxEndTime?: string;
  isOverdue?: boolean;
  startedAt?: string;
  onStop: () => void;
  onExtend: (additionalMinutes: number) => void;
  onReduce: (minutesToReduce: number) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: string | null;
}

export function UpdateChargingModal({ 
  chargerName, 
  estimatedEndTime,
  maxEndTime,
  isOverdue,
  startedAt,
  onStop, 
  onExtend, 
  onReduce, 
  onCancel, 
  isLoading, 
  error 
}: UpdateChargingModalProps) {
  const { t } = useTranslation();
  const [selectedAction, setSelectedAction] = useState<'extend' | 'reduce' | 'stop' | null>(null);
  const [extensionMinutes, setExtensionMinutes] = useState(20);
  const [reductionMinutes, setReductionMinutes] = useState(10);
  const [customMinutes, setCustomMinutes] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  // Calculate session progress
  const getSessionProgress = () => {
    if (!startedAt || !estimatedEndTime) return 0;
    const start = new Date(startedAt).getTime();
    const end = new Date(estimatedEndTime).getTime();
    const now = Date.now();
    const total = end - start;
    const elapsed = now - start;
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!estimatedEndTime) return 0;
    const end = new Date(estimatedEndTime).getTime();
    const now = Date.now();
    const remaining = Math.max(0, Math.floor((end - now) / 60000));
    return remaining;
  };

  const extensionPresets = [
    { minutes: 10, label: t('dialogs.updateCharging.presets.10'), subtitle: t('dialogs.updateCharging.subtitles.quick') },
    { minutes: 20, label: t('dialogs.updateCharging.presets.20'), subtitle: t('dialogs.updateCharging.subtitles.standard') },
    { minutes: 30, label: t('dialogs.updateCharging.presets.30'), subtitle: t('dialogs.updateCharging.subtitles.extended') },
    { minutes: 40, label: t('dialogs.updateCharging.presets.40'), subtitle: t('dialogs.updateCharging.subtitles.long') }
  ];

  const reductionPresets = [
    { minutes: 10, label: t('dialogs.updateCharging.presets.10'), subtitle: t('dialogs.updateCharging.subtitles.quickReduce') },
    { minutes: 20, label: t('dialogs.updateCharging.presets.20'), subtitle: t('dialogs.updateCharging.subtitles.standardReduce') },
    { minutes: 30, label: t('dialogs.updateCharging.presets.30'), subtitle: t('dialogs.updateCharging.subtitles.extendedReduce') },
    { minutes: 40, label: t('dialogs.updateCharging.presets.40'), subtitle: t('dialogs.updateCharging.subtitles.longReduce') }
  ];

  const handleAction = () => {
    if (selectedAction === 'stop') {
      onStop();
    } else if (selectedAction === 'extend') {
      const minutes = useCustom ? parseInt(customMinutes) || 20 : extensionMinutes;
      onExtend(minutes);
    } else if (selectedAction === 'reduce') {
      const minutes = useCustom ? parseInt(customMinutes) || 10 : reductionMinutes;
      onReduce(minutes);
    }
  };

  const formatEndTime = (timeStr?: string) => {
    if (!timeStr) return t('common.unknown');
    return new Date(timeStr).toLocaleTimeString();
  };

  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern">
        <div className="modal-header-modern">
          <div className="session-header-content">
            <h3 className="modal-title">
              <Zap size={20} style={{ color: isOverdue ? 'var(--color-error)' : 'var(--color-charging)' }} />
              {isOverdue ? 'Session Overdue' : t('dialogs.updateCharging.currentSession')}
            </h3>
            <p className="modal-subtitle">{chargerName}</p>
          </div>
          {/* Session Status Badge */}
          <div className={`session-status-badge ${isOverdue ? 'overdue' : 'active'}`}>
            {isOverdue ? '⚠️ Overdue' : '⚡ Active'}
          </div>
          <button
            className="modal-close-btn"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-content-modern">
          {/* Session Progress Section */}
          <div className="session-progress-section">
            <div className="progress-header">
              <span className="progress-label">Session Duration</span>
              <span className="time-remaining">
                {getTimeRemaining()} min remaining
              </span>
            </div>
            <div className="progress-bar-container">
              <div 
                className={`progress-bar ${isOverdue ? 'overdue' : 'active'}`}
                style={{ width: `${getSessionProgress()}%` }}
              />
            </div>
          </div>

          {/* Session Details Card */}
          <div className="current-session-info">
            {isOverdue && (
              <div className="overdue-warning-banner">
                <div className="overdue-icon">⚠️</div>
                <div className="overdue-text">
                  <strong>Session Overdue</strong>
                  <p>Your session has exceeded the maximum allowed time. Please end your session when you're done charging.</p>
                </div>
              </div>
            )}
            <div className="session-details">
              <div className="session-detail">
                <Zap size={16} style={{ color: isOverdue ? 'var(--color-error)' : 'var(--color-charging)' }} />
                <span>{isOverdue ? 'Session Overdue' : t('dialogs.updateCharging.chargingInProgress')}</span>
              </div>
              {(estimatedEndTime || maxEndTime) && (
                <div className="session-detail">
                  <Clock size={16} style={{ color: isOverdue ? 'var(--color-error)' : 'var(--color-queue)' }} />
                  <span>
                    {isOverdue 
                      ? `Max time exceeded: ${formatEndTime(maxEndTime)}`
                      : `${t('dialogs.updateCharging.estimatedEnd')}: ${formatEndTime(estimatedEndTime)}`
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="action-selection">
            <h4>{t('dialogs.updateCharging.whatToDo')}</h4>
            
            <div className="action-options">
              <button
                className={`action-option ${selectedAction === 'extend' ? 'action-option-active' : ''}`}
                onClick={() => setSelectedAction('extend')}
              >
                <div className="action-icon" style={{ backgroundColor: 'var(--color-primary-bg)' }}>
                  <Clock size={20} style={{ color: 'var(--color-primary)' }} />
                </div>
                <div className="action-details">
                  <div className="action-title">{t('dialogs.updateCharging.extendSession')}</div>
                  <div className="action-subtitle">{t('dialogs.updateCharging.addMoreTime')}</div>
                </div>
              </button>

              <button
                className={`action-option ${selectedAction === 'reduce' ? 'action-option-active' : ''}`}
                onClick={() => setSelectedAction('reduce')}
              >
                <div className="action-icon" style={{ backgroundColor: 'var(--color-warning-bg)' }}>
                  <Clock size={20} style={{ color: 'var(--color-warning)' }} />
                </div>
                <div className="action-details">
                  <div className="action-title">{t('dialogs.updateCharging.reduceSession')}</div>
                  <div className="action-subtitle">{t('dialogs.updateCharging.reduceTime')}</div>
                </div>
              </button>

              <button
                className={`action-option ${selectedAction === 'stop' ? 'action-option-active' : ''}`}
                onClick={() => setSelectedAction('stop')}
              >
                <div className="action-icon" style={{ backgroundColor: 'var(--color-error-bg)' }}>
                  <X size={20} style={{ color: 'var(--color-error)' }} />
                </div>
                <div className="action-details">
                  <div className="action-title">{t('dialogs.updateCharging.stopCharging')}</div>
                  <div className="action-subtitle">{t('dialogs.updateCharging.endSession')}</div>
                </div>
              </button>
            </div>
          </div>

          {selectedAction === 'extend' && (
            <div className="extension-options">
              <h4>{t('dialogs.updateCharging.howMuchTime')}</h4>
              
              <div className="time-presets-modern">
                {extensionPresets.map(({ minutes, label, subtitle }) => (
                  <button
                    key={minutes}
                    className={`preset-btn-modern ${!useCustom && extensionMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                    onClick={() => {
                      setExtensionMinutes(minutes);
                      setUseCustom(false);
                    }}
                  >
                    <div className="preset-label">{label}</div>
                    <div className="preset-subtitle">{subtitle}</div>
                  </button>
                ))}
              </div>

              <div className="custom-time-modern">
                <button
                  className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
                  onClick={() => setUseCustom(true)}
                >
                  <div className="custom-option-content">
                    <span className="custom-option-label">{t('dialogs.updateCharging.customTime')}</span>
                    {useCustom && (
                      <input
                        type="number"
                        value={customMinutes}
                        onChange={(e) => setCustomMinutes(e.target.value)}
                        placeholder={t('dialogs.updateCharging.minutesPlaceholder')}
                        min="1"
                        max="120"
                        className="custom-input-modern"
                        autoFocus
                      />
                    )}
                  </div>
                </button>
              </div>
            </div>
          )}

          {selectedAction === 'reduce' && (
            <div className="extension-options">
              <h4>{t('dialogs.updateCharging.howMuchReduce')}</h4>
              
              <div className="time-presets-modern">
                {reductionPresets.map(({ minutes, label, subtitle }) => (
                  <button
                    key={minutes}
                    className={`preset-btn-modern ${!useCustom && reductionMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                    onClick={() => {
                      setReductionMinutes(minutes);
                      setUseCustom(false);
                    }}
                  >
                    <div className="preset-label">{label}</div>
                    <div className="preset-subtitle">{subtitle}</div>
                  </button>
                ))}
              </div>

              <div className="custom-time-modern">
                <button
                  className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
                  onClick={() => setUseCustom(true)}
                >
                  <div className="custom-option-content">
                    <span className="custom-option-label">{t('dialogs.updateCharging.customTime')}</span>
                    {useCustom && (
                      <input
                        type="number"
                        value={customMinutes}
                        onChange={(e) => setCustomMinutes(e.target.value)}
                        placeholder={t('dialogs.updateCharging.minutesPlaceholder')}
                        min="1"
                        max="120"
                        className="custom-input-modern"
                        autoFocus
                      />
                    )}
                  </div>
                </button>
              </div>
            </div>
          )}

          {selectedAction === 'stop' && (
            <div className="stop-confirmation">
              <div className="warning-message">
                <div className="warning-icon">⚠️</div>
                <div className="warning-text">
                  <strong>{t('dialogs.updateCharging.confirmStop')}</strong>
                  <p>{t('dialogs.updateCharging.stopWarning')}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message-modern" style={{ margin: '16px 24px' }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="modal-actions-modern">
          <button
            className="btn-modern btn-secondary-modern"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn-modern btn-primary-modern"
            onClick={handleAction}
            disabled={isLoading || !selectedAction}
          >
            {isLoading ? t('common.processing') : 
             selectedAction === 'stop' ? t('dialogs.updateCharging.buttons.stopCharging') : 
             selectedAction === 'extend' ? t('dialogs.updateCharging.buttons.extendSession') : 
             selectedAction === 'reduce' ? t('dialogs.updateCharging.buttons.reduceSession') : 
             t('dialogs.updateCharging.buttons.selectAction')}
          </button>
        </div>
      </div>
    </div>
  );
}
